<template>
  <v-container fluid>
    <v-row align="center" justify="center">
      <v-col cols="12" sm="10" md="10">
        <v-card>
          <v-card-title>
            <v-icon large>mdi-radio-tower</v-icon>
            <h3>{{ $tt('caster.rtkInfo') }}</h3>
          </v-card-title>
          <v-card-title>
            <a :href="faqInfo.url" target="_blank" class="subtitle-1"> [FAQ] {{ faqInfo.title }} </a>
          </v-card-title>

          <v-tabs v-model="tab" show-arrows>
            <v-tab>{{ $tt('caster.statusTab') }}</v-tab>
            <v-tab>{{ $tt('caster.qualityTab') }}</v-tab>
            <v-tab>{{ $tt('caster.mapTab') }}</v-tab>
            <v-tab>{{ $tt('caster.logTab') }}</v-tab>
            <v-tab>{{ $tt('caster.settingsTab') }}</v-tab>
          </v-tabs>

          <v-tabs-items v-model="tab" touchless>
            <!-- ステータス -->
            <v-tab-item>
              <v-card-text>
                <caster-path-diagram :stats="casterStats" :track="casterTrack" />

                <v-card class="my-3">
                  <v-card-title>
                    <h5>{{ $tt('caster.myBaseStationStatus') }}</h5>
                  </v-card-title>
                  <v-card-text>
                    <v-sheet class="body-1 mx-3" v-if="bases.length === 0">({{ $tt('customNone') }})</v-sheet>
                    <v-simple-table v-if="bases.length !== 0">
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th class="text-left">
                              {{ $tt('caster.mountpoint') }}
                            </th>
                            <th class="text-left">
                              {{ $tt('customStatus') }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="rtkBase in bases" :key="rtkBase.mountpoint">
                            <td>{{ rtkBase.mountpoint }}</td>
                            <td>
                              <v-chip color="primary" v-if="rtkBase.isOnline()">
                                {{ rtkBase.getStatus() }}
                              </v-chip>
                              <v-chip v-else>
                                {{ rtkBase.getStatus() }}
                              </v-chip>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-card-text>
                </v-card>

                <caster-stats-summary :snapshot="latestSnapshot" :is-live="isSourceConnected" />
              </v-card-text>
            </v-tab-item>

            <!-- 品質グラフ -->
            <v-tab-item>
              <v-card-text>
                <caster-quality-charts :history="casterStatsHistory" :snapshot="latestSnapshot" />
              </v-card-text>
            </v-tab-item>

            <!-- 地図 -->
            <v-tab-item>
              <v-card-text>
                <caster-track-map :snapshot="latestSnapshot" :track="casterTrack" />
              </v-card-text>
            </v-tab-item>

            <!-- 接続ログ -->
            <v-tab-item>
              <v-card-text>
                <caster-connection-log :stats="casterStats" :track="casterTrack" />
              </v-card-text>
            </v-tab-item>

            <!-- 設定 (他社製品向け接続設定) -->
            <v-tab-item>
              <v-card-text>
                <v-card class="my-3" v-if="settingInfo">
                  <v-card-title>
                    <h5>{{ $tt('caster.howToSetupRtkBase') }}</h5>
                  </v-card-title>
                  <v-card-text>
                    <v-simple-table>
                      <tbody>
                        <tr>
                          <td class="col-5">{{ $tt('caster.host') }}</td>
                          <td>{{ settingInfo.host }}</td>
                        </tr>
                        <tr>
                          <td>{{ $tt('caster.port') }}</td>
                          <td>{{ settingInfo.port }}</td>
                        </tr>
                        <tr>
                          <td>{{ $tt('caster.mountpoint') }}</td>
                          <td>{{ settingInfo.mountpoint }}</td>
                        </tr>
                        <tr>
                          <td>{{ $tt('caster.baseStation') }}: {{ $tt('caster.userId') }}</td>
                          <td>({{ $tt('customNone') }})</td>
                        </tr>
                        <tr>
                          <td>{{ $tt('caster.baseStation') }}: {{ $tt('customPassword') }}</td>
                          <td>
                            {{ basePassword }}
                            <span @click="showBasePassword()" v-if="hideBasePassword"
                              >({{ $tt('caster.showCaption') }})</span
                            >
                          </td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-card-text>
                  <v-card-title>
                    <h5>{{ $tt('caster.howToSetupRtkRover') }}</h5>
                  </v-card-title>
                  <v-card-text>
                    <v-simple-table>
                      <tbody>
                        <tr>
                          <td class="col-5">{{ $tt('caster.host') }}</td>
                          <td>{{ settingInfo.host }}</td>
                        </tr>
                        <tr>
                          <td>{{ $tt('caster.port') }}</td>
                          <td>{{ settingInfo.port }}</td>
                        </tr>
                        <tr>
                          <td>
                            {{ $tt('caster.mountpoint')
                            }}<span class="caption">{{ $tt('caster.derivedPassthroughLabel') }}</span>
                            <div class="caption grey--text">{{ $tt('caster.derivedPassthroughCaption') }}</div>
                          </td>
                          <td>{{ settingInfo.mountpoint }}</td>
                        </tr>
                        <tr>
                          <td>
                            {{ $tt('caster.mountpoint')
                            }}<span class="caption">{{ $tt('caster.derivedMsm4Label') }}</span>
                            <div class="caption grey--text">{{ $tt('caster.derivedMsm4Caption') }}</div>
                          </td>
                          <td>{{ settingInfo.mountpoint }}_MSM4</td>
                        </tr>
                        <tr>
                          <td>
                            {{ $tt('caster.mountpoint')
                            }}<span class="caption">{{ $tt('caster.derivedLegacyLabel') }}</span>
                            <div class="caption grey--text">{{ $tt('caster.derivedLegacyCaption') }}</div>
                          </td>
                          <td>{{ settingInfo.mountpoint }}_LEGACY</td>
                        </tr>
                        <tr>
                          <td>{{ $tt('caster.ntripUserId') }}</td>
                          <td>{{ settingInfo.roverUserId }}</td>
                        </tr>
                        <tr>
                          <td>{{ $tt('caster.ntripPassword') }}</td>
                          <td>
                            {{ roverPassword }}
                            <span @click="showRoverPassword()" v-if="hideRoverPassword"
                              >({{ $tt('caster.showCaption') }})</span
                            >
                          </td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                    <div class="caption grey--text mt-2">{{ $tt('caster.derivedNote') }}</div>
                    <div class="subtitle-2 mt-4">{{ $tt('caster.messageTypesTitle') }}</div>
                    <div class="caption grey--text">{{ $tt('caster.messageTypesIntro') }}</div>
                    <v-simple-table dense class="mt-1">
                      <tbody>
                        <tr>
                          <td class="col-5">
                            {{ settingInfo.mountpoint
                            }}<span class="caption">{{ $tt('caster.derivedPassthroughLabel') }}</span>
                          </td>
                          <td class="caption">{{ $tt('caster.messageTypesPassthrough') }}</td>
                        </tr>
                        <tr>
                          <td>
                            {{ settingInfo.mountpoint }}_MSM4<span class="caption">{{
                              $tt('caster.derivedMsm4Label')
                            }}</span>
                          </td>
                          <td class="caption">{{ $tt('caster.messageTypesMsm4') }}</td>
                        </tr>
                        <tr>
                          <td>
                            {{ settingInfo.mountpoint }}_LEGACY<span class="caption">{{
                              $tt('caster.derivedLegacyLabel')
                            }}</span>
                          </td>
                          <td class="caption">{{ $tt('caster.messageTypesLegacy') }}</td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-card-text>
                </v-card>
              </v-card-text>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import * as Constants from '@/lib/constants'
import UserInfo from '@/lib/user-info'
import CasterPathDiagram from '@/components/caster/CasterPathDiagram'
import CasterStatsSummary from '@/components/caster/CasterStatsSummary'
import CasterQualityCharts from '@/components/caster/CasterQualityCharts'
import CasterTrackMap from '@/components/caster/CasterTrackMap'
import CasterConnectionLog from '@/components/caster/CasterConnectionLog'

export default {
  name: 'CasterInfo',
  components: {
    CasterPathDiagram,
    CasterStatsSummary,
    CasterQualityCharts,
    CasterTrackMap,
    CasterConnectionLog,
  },
  data() {
    return {
      tab: 0,
      listCasterDaemon: null,
      statsDaemon: null,
      historyDaemon: null,
      hideBasePassword: true,
      hideRoverPassword: true,
    }
  },
  mounted() {
    this.$store.dispatch('authGetRtkSettingInfo')
    this.listCasterBasesAndRovers()
    this.listCasterDaemon = setInterval(
      this.listCasterBasesAndRovers,
      Constants.LIST_CASTER_BASES_AND_ROVERS_INTERVAL_SECONDS * 1000
    )
    // 補正情報モニタリング: 現在値は15秒、履歴と軌跡は60秒間隔で更新
    this.refreshHistoryAndTrack()
    this.statsDaemon = setInterval(() => {
      this.$store.dispatch('getCasterStats')
    }, Constants.CASTER_STATS_INTERVAL_SECONDS * 1000)
    this.historyDaemon = setInterval(
      this.refreshHistoryAndTrack,
      Constants.CASTER_STATS_HISTORY_INTERVAL_SECONDS * 1000
    )
    this.hideBasePassword = true
    this.hideRoverPassword = true
  },
  destroyed() {
    clearInterval(this.listCasterDaemon)
    clearInterval(this.statsDaemon)
    clearInterval(this.historyDaemon)
  },
  computed: {
    settingInfo() {
      return this.$store.getters.rtkSettingInfo
    },
    bases() {
      return this.$store.getters.rtkBases
    },
    rovers() {
      return this.$store.getters.rtkRovers
    },
    casterStats() {
      return this.$store.getters.casterStats
    },
    casterStatsHistory() {
      return this.$store.getters.casterStatsHistory
    },
    casterTrack() {
      return this.$store.getters.casterTrack
    },
    /** 現在接続中のスナップショット、なければ直近切断時の最終スナップショット */
    latestSnapshot() {
      if (!this.casterStats) {
        return null
      }
      return this.casterStats.current || (this.casterStats.lastEnded && this.casterStats.lastEnded.snapshot) || null
    },
    isSourceConnected() {
      return !!(this.casterStats && this.casterStats.current)
    },
    basePassword() {
      if (this.hideBasePassword) {
        return '●●●●●●'
      }
      return this.settingInfo.basePassword
    },
    roverPassword() {
      if (this.hideRoverPassword) {
        return '●●●●●●'
      }
      return this.settingInfo.roverPassword
    },
    faqInfo() {
      const lang = UserInfo.getLanguage()
      switch (lang) {
        case 'ja':
          return {
            title: '補正情報を受信するにはどんな方法がありますか？',
            url:
              'https://support.agri-info-design.com/hc/ja/articles/4407882995353--%E4%B8%80%E8%88%AC-%E8%A3%9C%E6%AD%A3%E6%83%85%E5%A0%B1%E3%82%92%E5%8F%97%E4%BF%A1%E3%81%99%E3%82%8B%E3%81%AB%E3%81%AF%E3%81%A9%E3%82%93%E3%81%AA%E6%96%B9%E6%B3%95%E3%81%8C%E3%81%82%E3%82%8A%E3%81%BE%E3%81%99%E3%81%8B-',
          }
        case 'de':
          return {
            title: 'Wie erhalte ich am besten Korrekturdaten?',
            url:
              'https://support.agri-info-design.com/hc/de/articles/4407882995353-Wie-erhalte-ich-am-besten-Korrekturdaten-',
          }
        case 'es':
          return {
            title: '¿Cuál es la mejor manera de recibir información sobre la corrección?',
            url:
              'https://support.agri-info-design.com/hc/es/articles/4407882995353--Cu%C3%A1l-es-la-mejor-manera-de-recibir-informaci%C3%B3n-sobre-la-correcci%C3%B3n-',
          }
        case 'pl':
          return {
            title: 'Jaki jest najlepszy sposób otrzymywania informacji o poprawkach?',
            url:
              'https://support.agri-info-design.com/hc/pl-pl/articles/4407882995353-Jaki-jest-najlepszy-spos%C3%B3b-otrzymywania-informacji-o-poprawkach-',
          }
        case 'pt':
          return {
            title: 'Qual é a melhor maneira de receber informações de correção?',
            url:
              'https://support.agri-info-design.com/hc/pt-br/articles/4407882995353-Qual-%C3%A9-a-melhor-maneira-de-receber-informa%C3%A7%C3%B5es-de-corre%C3%A7%C3%A3o-',
          }
        case 'ru':
          return {
            title: 'Каков наилучший способ получения информации о коррекции?',
            url:
              'https://support.agri-info-design.com/hc/ru/articles/4407882995353-%D0%9A%D0%B0%D0%BA%D0%BE%D0%B2-%D0%BD%D0%B0%D0%B8%D0%BB%D1%83%D1%87%D1%88%D0%B8%D0%B9-%D1%81%D0%BF%D0%BE%D1%81%D0%BE%D0%B1-%D0%BF%D0%BE%D0%BB%D1%83%D1%87%D0%B5%D0%BD%D0%B8%D1%8F-%D0%B8%D0%BD%D1%84%D0%BE%D1%80%D0%BC%D0%B0%D1%86%D0%B8%D0%B8-%D0%BE-%D0%BA%D0%BE%D1%80%D1%80%D0%B5%D0%BA%D1%86%D0%B8%D0%B8-',
          }
        case 'en': // default扱い
        default:
          return {
            title: 'What is the best way to receive correction information?',
            url:
              'https://support.agri-info-design.com/hc/en-us/articles/4407882995353-What-is-the-best-way-to-receive-correction-information-',
          }
      }
    },
  },
  methods: {
    listCasterBasesAndRovers() {
      this.$store.dispatch('authListCasterBasesAndRovers')
    },
    refreshHistoryAndTrack() {
      this.$store.dispatch('getCasterStats', { historyMinutes: Constants.CASTER_STATS_HISTORY_MINUTES })
      this.$store.dispatch('getCasterTrack')
    },
    showBasePassword() {
      this.hideBasePassword = false
    },
    showRoverPassword() {
      this.hideRoverPassword = false
    },
  },
}
</script>
